import { reactions, type Reaction } from "@/data/reactions";

/**
 * Reaction Race - pure question-generation logic.
 *
 * Two prompt kinds:
 *  - "predict-product"  : reactants shown, pick the correct product set
 *  - "predict-type"     : full equation shown, pick the reaction type
 *
 * Both use 4-choice multiple-choice with one correct answer drawn from the
 * 51-reaction bank in `src/data/reactions.ts`. Distractors are sampled from
 * the same bank so the choices are always plausible chemistry strings rather
 * than obviously fake data.
 */

export type RaceQuestionKind = "predict-product" | "predict-type";

export interface RaceQuestion {
  id: string;
  kind: RaceQuestionKind;
  prompt: string;
  /** Optional sub-line shown in monospace under the prompt (the reactants or full equation). */
  formula: string;
  choices: [string, string, string, string];
  correctIndex: 0 | 1 | 2 | 3;
  explanation: string;
  /** Longer pedagogical reasoning shown in the "Why this is correct" disclosure. */
  whyCorrect: string;
}

export const RACE_TURN_SECONDS = 12;
export const FAST_THRESHOLD_S = 4;
export const POINTS_FAST = 15;
export const POINTS_SLOW = 8;
export const POINTS_WRONG = 0;
export const POINTS_TIMEOUT = 0;

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

function sampleN<T>(rng: () => number, arr: T[], count: number): T[] {
  const copy = arr.slice();
  const out: T[] = [];
  while (copy.length > 0 && out.length < count) {
    const i = Math.floor(rng() * copy.length);
    out.push(copy.splice(i, 1)[0]);
  }
  return out;
}

function shuffleWithIndex<T>(rng: () => number, arr: T[], correct: T): { items: T[]; index: 0 | 1 | 2 | 3 } {
  const indexed = arr.map((v) => v);
  for (let i = indexed.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
  }
  const idx = indexed.indexOf(correct);
  return { items: indexed, index: idx as 0 | 1 | 2 | 3 };
}

const ALL_TYPE_LABELS: string[] = Array.from(new Set(reactions.map((r) => r.typeLabel)));

const productSetLabel = (r: Reaction): string => r.products.join(" + ");
const reactantSetLabel = (r: Reaction): string => r.reactants.join(" + ");

/** Generate one question, trying to honour `excludeIds` then falling back. */
export function generateRaceQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
): RaceQuestion {
  const KINDS: RaceQuestionKind[] = ["predict-product", "predict-type"];

  for (let attempt = 0; attempt < 8; attempt++) {
    const kind = pickRandom(rng, KINDS);
    const target = pickRandom(rng, reactions);
    const id = `${kind}:${target.id}`;
    if (excludeIds.has(id) && attempt < 7) continue;
    const q = build(rng, kind, target, id);
    if (q) return q;
  }
  // Last resort
  const target = pickRandom(rng, reactions);
  return build(rng, "predict-type", target, `predict-type:${target.id}`)!;
}

function build(
  rng: () => number,
  kind: RaceQuestionKind,
  target: Reaction,
  id: string,
): RaceQuestion | null {
  if (kind === "predict-product") {
    const correct = productSetLabel(target);
    // Distractor product sets from OTHER reactions, deduped & not equal to correct.
    const others = reactions.filter((r) => r.id !== target.id);
    const candidates: string[] = [];
    for (const r of others) {
      const label = productSetLabel(r);
      if (label !== correct && !candidates.includes(label)) candidates.push(label);
    }
    if (candidates.length < 3) return null;
    const distractors = sampleN(rng, candidates, 3);
    const { items, index } = shuffleWithIndex(rng, [correct, ...distractors], correct);
    return {
      id,
      kind,
      prompt: `What's the product of this ${target.typeLabel.toLowerCase()} reaction?`,
      formula: `${reactantSetLabel(target)} → ?`,
      choices: items as [string, string, string, string],
      correctIndex: index,
      explanation: `${target.equation}. ${target.description}`,
      whyCorrect: `Balanced equation: ${target.equation}. ${target.description} This is a ${target.typeLabel.toLowerCase()} reaction (${typeHint(target.type)}), and it's ${target.energy} - energy is ${target.energy === "exothermic" ? "released to" : "absorbed from"} the surroundings. Observation: ${target.observation.toLowerCase()}.`,
    };
  }

  // predict-type
  const correct = target.typeLabel;
  const distractorPool = ALL_TYPE_LABELS.filter((t) => t !== correct);
  if (distractorPool.length < 3) return null;
  const distractors = sampleN(rng, distractorPool, 3);
  const { items, index } = shuffleWithIndex(rng, [correct, ...distractors], correct);
  return {
    id,
    kind: "predict-type",
    prompt: "Which type of reaction is this?",
    formula: target.equation,
    choices: items as [string, string, string, string],
    correctIndex: index,
    explanation: `${target.description} (${target.energy}).`,
    whyCorrect: `${target.equation} is a ${target.typeLabel.toLowerCase()} reaction. ${typeHint(target.type)} ${target.description} It's ${target.energy}, so energy is ${target.energy === "exothermic" ? "released" : "absorbed"} during the reaction.`,
  };
}

/** Short pedagogical hint about a reaction type, used inside whyCorrect strings. */
function typeHint(type: Reaction["type"]): string {
  switch (type) {
    case "synthesis":
      return "Two or more substances combine to form a single product (A + B → AB).";
    case "decomposition":
      return "A single compound breaks down into two or more simpler substances (AB → A + B).";
    case "single-replacement":
      return "A more reactive element displaces a less reactive one from a compound (A + BC → AC + B).";
    case "double-replacement":
      return "Cations and anions of two compounds swap partners (AB + CD → AD + CB), often forming a precipitate, gas, or water.";
    case "combustion":
      return "A fuel reacts rapidly with oxygen, releasing heat and light - typically producing CO₂ and H₂O.";
    case "acid-base":
      return "An acid and a base neutralise each other to produce a salt and water (HA + BOH → BA + H₂O).";
  }
}

export function scoreRaceAnswer(
  chosenIndex: number | null,
  question: RaceQuestion,
  elapsedMs: number,
): { points: number; outcome: "fast" | "slow" | "wrong" | "timeout" } {
  if (chosenIndex === null) return { points: POINTS_TIMEOUT, outcome: "timeout" };
  if (chosenIndex !== question.correctIndex) return { points: POINTS_WRONG, outcome: "wrong" };
  if (elapsedMs <= FAST_THRESHOLD_S * 1000) return { points: POINTS_FAST, outcome: "fast" };
  return { points: POINTS_SLOW, outcome: "slow" };
}
